import { waitForShadowLayout } from './wait-for-layout';

export const waitForContent = (
  selector: string,
  onContentVisible?: (elements: HTMLElement[]) => void,
  container?: HTMLElement,
  maxFrames = 600
) => {
  let rafId: number | null = null;
  let isCancelled = false;
  let frameCount = 0;

  const check = () => {
    if (isCancelled) return;

    // Exit early if provided container was removed from DOM.
    if (container && !container.isConnected) {
      return;
    }

    const selectedElements: HTMLElement[] = Array.from(
      (container || document).querySelectorAll(selector)
    );

    // Check if we have the elements AND they're visible in viewport.
    const hasVisibleFields = selectedElements.some((element) => {
      const isHostVisible = isElementVisibleInViewport(
        element.getBoundingClientRect()
      );

      // If host is visible, also check if shadow root content is rendered
      if (isHostVisible && element.shadowRoot) {
        const shadowContent = element.shadowRoot.querySelector('*');
        if (shadowContent) {
          return isElementVisibleInViewport(
            shadowContent.getBoundingClientRect()
          );
        }
        return false; // Shadow root exists but no content yet
      }

      return isHostVisible;
    });

    if (hasVisibleFields) {
      onContentVisible?.(selectedElements);
      return;
    }

    if (frameCount >= maxFrames) {
      return;
    }

    frameCount += 1;
    rafId = requestAnimationFrame(check);
  };

  check();

  return () => {
    isCancelled = true;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };
};

export const waitForShadowContent = (
  hostSelector: string,
  shadowSelector: string,
  onContentVisible?: (shadowElements: Element[]) => void,
  container?: HTMLElement,
  maxFrames = 600
) => {
  let rafId: number | null = null;
  let isCancelled = false;
  let frameCount = 0;

  const check = () => {
    if (isCancelled) return;

    // Exit early if provided container was removed from DOM.
    if (container && !container.isConnected) {
      return;
    }

    const hostElements: HTMLElement[] = Array.from(
      (container || document).querySelectorAll(hostSelector)
    );

    const shadowElements: Element[] = [];

    // Collect all matching elements from shadow roots
    hostElements.forEach((host) => {
      if (host.shadowRoot) {
        const elements = Array.from(
          host.shadowRoot.querySelectorAll(shadowSelector)
        );
        shadowElements.push(...elements);
      }
    });

    // Check if shadow content is visible.
    const hasVisibleShadowContent = shadowElements.some((element) => {
      return isElementVisibleInViewport(element.getBoundingClientRect());
    });

    if (hasVisibleShadowContent && shadowElements.length > 0) {
      onContentVisible?.(shadowElements);
      return;
    }

    if (frameCount >= maxFrames) {
      return;
    }

    frameCount += 1;
    rafId = requestAnimationFrame(check);
  };

  check();

  return () => {
    isCancelled = true;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };
};

export const stripPofMargins = (
  container?: HTMLElement,
  margin?: 'mt' | 'mb'
) => {
  function removeMargins(shadowElements: Element[]) {
    shadowElements.forEach((p) => {
      if (p instanceof HTMLElement) {
        if (!margin || margin === 'mt') p.style.marginTop = '0';
        if (!margin || margin === 'mb') p.style.marginBottom = '0';
      }
    });
  }
  // Use a layout-stable wait (width/height > 0) rather than a viewport check
  // so margin stripping runs for off-screen cards (e.g., in paginated lists).
  return waitForShadowLayout('goa-text', 'p', removeMargins, container);
};

function isElementVisibleInViewport(rect: DOMRect): boolean {
  if (rect.width <= 0 || rect.height <= 0) {
    return false;
  }

  const verticalOverlap = rect.bottom > 0 && rect.top < window.innerHeight;
  const horizontalOverlap = rect.right > 0 && rect.left < window.innerWidth;

  return verticalOverlap && horizontalOverlap;
}
